export const isSafeInternalPath = (path) => {
  return typeof path === 'string' && /^\/(?!\/)/.test(path) && !path.includes('://')
}

const lastPathSegment = (path = '') => `${path}`.replace(/\/+$/, '').split('/').pop()

/**
 * True when `pathname` is one of the routes reached once the activation has
 * been submitted — process loader, process failure or transaction summary.
 * Those pages render the computed totals and promo details straight from
 * Redux, so callers use this to keep that state alive on the way in.
 *
 * Matched on the last path segment so one check covers the consumer and
 * dealer paths whatever locale/province prefix they carry.
 */
export const isActivationOutcomePath = (pathname, ...activationPaths) => {
  const segment = lastPathSegment(pathname)
  if (!segment) return false

  return activationPaths.some((paths) => {
    return [paths?.ProcessLoader, paths?.ProcessFailure, paths?.TransactionSummary]
      .some((path) => path && lastPathSegment(path) === segment)
  })
}

export const navigateToPath = (
  currentState,
  activationPaths,
  history,
  lang = 'en',
  prov = 'on',
  localizedPlanItem = {},
  pricePlanId = ''
) => {
  return new Promise((resolve) => {
    const url = history?.location?.pathname
    if (url) {
      if (!isValidUrlEnding(url, lang, prov)) {
        history.push(url)
      } else if (currentState === 'Sim Selection') {
        history.push(activationPaths.SimSelection)
      } else if (currentState === 'Account Creation') {
        history.push(activationPaths.AccountSetup)
      } else if (currentState === 'Phone Number Selection') {
        history.push(activationPaths.ChooseNumber)
      } else if (currentState === 'Plan Selection') {
        history.push(activationPaths.Plans)
      } else if (currentState === 'Payment Information') {
        const langPlans = localizedPlanItem?.[lang]
        const plansObject = langPlans?.find(Boolean) || {}
        const { plans = [] } = plansObject
        const currentPlan = plans?.find((planData) => planData?.id === pricePlanId)
        if (
          pricePlanId &&
          pricePlanId !== 'PLAN_NOT_SELECTED' &&
          plans.length > 0 &&
          !currentPlan
        ) {
          // The quote's plan is no longer in the catalog (retired), so send the
          // user to the plans page to pick a current one rather than to a
          // payment page priced on a plan that is no longer sold.
          //
          // Deliberately no `from: Payment`: that would point the plans page's
          // back link straight back at the stale payment page — the hazard the
          // old HIDE_BACK_BUTTON flag existed to prevent. Without it the back
          // link falls back to ChooseNumber, and selecting a plan still returns
          // to Payment via handleAddToCart's own fallback.
          history.push(activationPaths.Plans)
        } else {
          history.push(activationPaths.Payment)
        }
      } else if (currentState === 'Download App') {
        // A finished quote does not make the user activated. Only the
        // KPRE_UCARE_ACCESS role does, and those users are routed to
        // AlreadyActivated by the landing logic before the quote is ever read
        // (getRetrieveAccountByEmail sets isAlreadyActivated, which gates the
        // quote path), so anyone reaching here holds KPRE_AP_ACCESS only —
        // their account was never provisioned. Send them to Splash to restart
        // rather than telling them they are already activated (MASC-17321).
        history.push(activationPaths.Splash)
      } else {
        history.push(activationPaths.Splash)
      }
    }
    setTimeout(resolve, 0)
  })
}

const isValidUrlEnding = (url, lang, prov) => {
  const validUrlEndings = [
    '/activation',
    '/activation/plans',
    '/activation/sim-selection',
    '/activation/account-setup',
    '/activation/payment',
    `/${lang}/${prov}`
  ]
  const validUrlEndingWithSlash = validUrlEndings.map((urlEnding) => urlEnding.concat('/'))
  validUrlEndings.concat(validUrlEndingWithSlash).some((urlEnding) => url.endsWith(urlEnding))
  return validUrlEndings
}
